import { useState } from 'react'
import type { SavedScenarios as SavedScenariosType, Scenario } from '../types'
import type React from 'react'

function getTagLabel(tag?: string) {
  if (!tag) return null
  const labels: Record<string, string> = {
    baseline: 'Most Likely',
    tail_risk: 'Tail Risk',
    marginal_yes: 'Close Call (Yes)',
    marginal_no: 'Close Call (No)',
  }
  return labels[tag] || tag
}

function getTagStyle(tag?: string): React.CSSProperties {
  if (!tag) return {}
  const styles: Record<string, React.CSSProperties> = {
    baseline: { backgroundColor: 'var(--green)', color: 'white' },
    tail_risk: { backgroundColor: 'var(--clay-red)', color: 'white' },
    marginal_yes: { backgroundColor: '#f59e0b', color: 'white' },
    marginal_no: { backgroundColor: '#f59e0b', color: 'white' },
  }
  return styles[tag] || {}
}

function ScenariosDisplay({ scenarios }: { scenarios: Scenario[] }) {
  const [selectedScenarioIndex, setSelectedScenarioIndex] = useState(0)

  if (scenarios.length === 0) return null

  return (
    <div className="mt-3">
      {/* Scenario selector tabs */}
      <div className="flex gap-2 flex-wrap">
        {scenarios.map((scenario, index) => {
          const isActive = index === selectedScenarioIndex
          const baseColor = scenario.outcome_occurs
            ? isActive ? 'border-green/50 bg-green/15' : 'border-green/30 bg-green/5'
            : isActive ? 'border-clay-red/60 bg-clay-red/20' : 'border-clay-red/40 bg-clay-red/10'
          const borderWeight = scenario.outcome_occurs ? 'border' : 'border-2'

          return (
            <button
              key={index}
              type="button"
              className={`rounded-delphi ${borderWeight} px-3 py-2 text-left transition hover:brightness-95 ${baseColor}`}
              onClick={() => setSelectedScenarioIndex(index)}
            >
              <div className="text-[10px] text-muted font-semibold tracking-wider">{index + 1}</div>
              <div className="text-sm font-semibold text-ink mt-0.5 leading-tight">{scenario.title}</div>
              {scenario.tag && (
                <div
                  className="mt-1 text-[10px] font-semibold tracking-wider rounded px-1.5 py-0.5 inline-block"
                  style={getTagStyle(scenario.tag)}
                >
                  {getTagLabel(scenario.tag)}
                </div>
              )}
            </button>
          )
        })}
      </div>

      {/* Selected scenario content */}
      {scenarios[selectedScenarioIndex] && (
        <div
          className={`mt-3 rounded-delphi border-2 p-4 ${
            scenarios[selectedScenarioIndex].outcome_occurs
              ? 'border-green/40 bg-green/5'
              : 'border-clay-red/40 bg-clay-red/5'
          }`}
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-semibold text-lg text-ink">{scenarios[selectedScenarioIndex].title}</div>
              {scenarios[selectedScenarioIndex].tag && (
                <div
                  className="mt-1 text-[10px] font-semibold tracking-wider rounded px-2 py-1 inline-block"
                  style={getTagStyle(scenarios[selectedScenarioIndex].tag)}
                >
                  {getTagLabel(scenarios[selectedScenarioIndex].tag)}
                </div>
              )}
            </div>
            <div
              className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                scenarios[selectedScenarioIndex].outcome_occurs
                  ? 'bg-green text-white'
                  : 'text-white'
              }`}
              style={!scenarios[selectedScenarioIndex].outcome_occurs ? { backgroundColor: '#a84848' } : {}}
            >
              {scenarios[selectedScenarioIndex].outcome_occurs ? '✓ Outcome occurs' : '✗ Outcome does not occur'}
            </div>
          </div>

          <p className="mt-3 text-sm text-ink/90 leading-relaxed">{scenarios[selectedScenarioIndex].summary}</p>

          <div className="mt-4">
            <div className="text-xs font-semibold text-muted tracking-wider mb-2">HOW IT UNFOLDS:</div>
            <ul className="space-y-2">
              {scenarios[selectedScenarioIndex].narrative_points.map((point, idx) => (
                <li key={idx} className="text-sm text-ink/90 leading-relaxed pl-4 relative">
                  <span className="absolute left-0 text-muted font-semibold">→</span>
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

function labelForItem(item: SavedScenariosType): string {
  const dt = item.created_at ? new Date(item.created_at) : null
  const stamp = dt && !Number.isNaN(dt.getTime()) ? dt.toLocaleString() : 'Unknown time'
  const prob = `${Math.round(item.probability * 100)}%`
  return `Scenarios (${prob}) • ${stamp}`
}

export default function SavedScenarios({ items }: { items: SavedScenariosType[] }) {
  const [expandedId, setExpandedId] = useState<string | null>(null)

  if (!items.length) return null

  return (
    <div className="mt-3 border-t border-rule pt-3">
      <div className="text-xs text-muted tracking-wider font-semibold">SAVED SCENARIOS</div>
      <div className="mt-2 text-xs text-muted leading-relaxed">
        Illustrative scenarios showing how this forecast outcome might (or might not) unfold.
      </div>
      <div className="mt-2 space-y-2">
        {items.slice(0, 6).map((item) => {
          const isExpanded = expandedId === item.scenario_id
          return (
            <div key={item.scenario_id} className="rounded-delphi border border-rule bg-paper">
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-xs text-ink/90 hover:bg-white/40 transition flex items-center justify-between gap-2"
                onClick={() => setExpandedId(isExpanded ? null : item.scenario_id)}
              >
                <span className="truncate">{labelForItem(item)}</span>
                <span className="text-[10px]">{isExpanded ? '▾' : '▸'}</span>
              </button>
              {isExpanded && (
                <div className="px-3 pb-3">
                  <ScenariosDisplay scenarios={item.scenarios} />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
